import {
  ArrowLeftOutlined,
  CloseOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { Button, Modal, Space, Spin, Table, Tag, Typography } from "antd";
import { format } from "date-fns";
import { useCallback, useMemo, useState } from "react";
import { color, font, radius } from "theme/aiInventory";

import { EmptyState } from "../EmptyState.tsx";
import type { SessionSummary } from "../graph/types.ts";
import type { ManagedMcpServer } from "../hooks/useManagedMcpServers.ts";
import type { McpCallRecord } from "../hooks/useMcpAccessLogs.ts";
import { useScopedMcpCallRecords } from "../hooks/useScopedMcpCallRecords.ts";
import { useToolsForServers } from "../hooks/useToolsForServers.ts";

const kindLabel = (kind: SessionSummary["kind"]): string =>
  kind === "tool-call" ? "TOOL CALL" : "REQUEST";

const kindColor = (kind: SessionSummary["kind"]): string =>
  kind === "tool-call" ? "blue" : "default";

type SessionRow = {
  readonly key: string;
  readonly sessionId: string;
  readonly summary: SessionSummary;
};

const ViewJsonLink: React.FC<{
  summary: SessionSummary;
  onOpen: (summary: SessionSummary) => void;
}> = ({ summary, onOpen }) => {
  const handle = useCallback(() => onOpen(summary), [onOpen, summary]);
  return <Typography.Link onClick={handle}>View JSON</Typography.Link>;
};

const rowStyle = { cursor: "pointer" } as const;

export type ServerSidecarProps = {
  server: ManagedMcpServer;
  onClose: () => void;
  onBack?: () => void;
};

export const ServerSidecar: React.FC<ServerSidecarProps> = ({
  server,
  onClose,
  onBack,
}) => {
  const { records, loading, refetch } = useScopedMcpCallRecords({
    server,
    maxRecords: 50,
  });
  const serverList = useMemo(() => [server], [server]);
  const { toolsByServer } = useToolsForServers(serverList);
  const [openRecord, setOpenRecord] = useState<McpCallRecord | undefined>();
  const handleCloseRecord = useCallback(() => setOpenRecord(undefined), []);
  const openSummary = useCallback(
    (summary: SessionSummary) => {
      setOpenRecord(records.find((r) => r.summary.id === summary.id));
    },
    [records]
  );

  const sessionRows = useMemo<SessionRow[]>(
    () =>
      records.map((record) => ({
        key: record.summary.id,
        sessionId: record.sessionId,
        summary: record.summary,
      })),
    [records]
  );

  const sessionColumns = useMemo(
    () => [
      {
        title: "Time",
        dataIndex: "summary",
        key: "time",
        width: 150,
        render: (summary: SessionSummary) => (
          <Typography.Text
            type="secondary"
            style={{ fontFamily: font.mono, fontSize: 11 }}
            title={summary.eventAt}
          >
            {format(new Date(summary.eventAt), "MMM d, HH:mm:ss")}
          </Typography.Text>
        ),
      },
      {
        title: "Activity",
        dataIndex: "summary",
        key: "activity",
        render: (summary: SessionSummary) => (
          <Typography.Text>{summary.toolName}</Typography.Text>
        ),
      },
      {
        title: "Type",
        dataIndex: "summary",
        key: "type",
        width: 110,
        render: (summary: SessionSummary) => (
          <Tag color={kindColor(summary.kind)}>{kindLabel(summary.kind)}</Tag>
        ),
      },
      {
        title: "User",
        dataIndex: "summary",
        key: "user",
        render: (summary: SessionSummary) => (
          <Typography.Text
            style={{ fontFamily: font.mono, fontSize: 11 }}
            ellipsis
          >
            {summary.userEmail}
          </Typography.Text>
        ),
      },
      {
        title: "Session",
        dataIndex: "sessionId",
        key: "session",
        width: 110,
        render: (sessionId: string) => (
          <Typography.Text
            type="secondary"
            style={{ fontFamily: font.mono, fontSize: 11 }}
          >
            {sessionId.slice(0, 8)}
          </Typography.Text>
        ),
      },
      {
        title: "",
        dataIndex: "summary",
        key: "action",
        width: 90,
        render: (summary: SessionSummary) => (
          <ViewJsonLink summary={summary} onOpen={openSummary} />
        ),
      },
    ],
    [openSummary]
  );

  const handleRow = useCallback(
    (row: SessionRow) => ({
      onClick: () => openSummary(row.summary),
      style: rowStyle,
    }),
    [openSummary]
  );

  const toolCallCount = useMemo(
    () => records.filter((r) => r.summary.kind === "tool-call").length,
    [records]
  );

  const requestCount = useMemo(
    () => records.filter((r) => r.summary.kind !== "tool-call").length,
    [records]
  );

  const sessionCount = useMemo(() => {
    const sessions = new Set<string>();
    for (const r of records) sessions.add(r.sessionId);
    return sessions.size;
  }, [records]);

  const availableTools = useMemo(
    () => [...(toolsByServer.get(server.id) ?? new Set<string>())].sort(),
    [server.id, toolsByServer]
  );

  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      <Space style={{ justifyContent: "space-between", width: "100%" }}>
        {onBack ? (
          <Button type="text" icon={<ArrowLeftOutlined />} onClick={onBack}>
            Back
          </Button>
        ) : (
          <span />
        )}
        <Button type="text" icon={<CloseOutlined />} onClick={onClose} />
      </Space>

      <Space size={8} align="center">
        <Typography.Title level={4} style={{ margin: 0 }}>
          {server.label || server.id}
        </Typography.Title>
        <Tag color="green">MCP Server</Tag>
        {server.type && server.type !== "unknown" && <Tag>{server.type}</Tag>}
      </Space>

      <div
        style={{
          border: `1px solid ${color.rule}`,
          borderRadius: radius.card,
          padding: 12,
          fontFamily: font.mono,
          fontSize: 11,
          color: color.inkMute,
          display: "grid",
          gridTemplateColumns: "auto 1fr",
          gap: "4px 12px",
        }}
      >
        <span>id</span>
        <span style={{ color: color.ink }}>{server.id}</span>
        <span>path</span>
        <span style={{ color: color.ink }}>{server.path}</span>
        {server.upstream && (
          <>
            <span>upstream</span>
            <span style={{ color: color.ink }}>{server.upstream}</span>
          </>
        )}
        {server.url && (
          <>
            <span>url</span>
            <span style={{ color: color.ink }}>{server.url}</span>
          </>
        )}
      </div>

      {availableTools.length > 0 && (
        <div>
          <Space size={6} align="center" style={{ marginBottom: 6 }}>
            <Typography.Title level={5} style={{ margin: 0 }}>
              Available tools
            </Typography.Title>
            <Typography.Text
              type="secondary"
              style={{ fontFamily: font.mono, fontSize: 11 }}
            >
              {availableTools.length} advertised by tools/list
            </Typography.Text>
          </Space>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {availableTools.map((t) => (
              <Tag key={t} style={{ fontFamily: font.mono, margin: 0 }}>
                {t}
              </Tag>
            ))}
          </div>
        </div>
      )}

      <div>
        <Space
          size={8}
          align="center"
          style={{
            marginBottom: 8,
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <Space size={8} align="center">
            <Typography.Title level={5} style={{ margin: 0 }}>
              Recent sessions
            </Typography.Title>
            {sessionRows.length > 0 && (
              <Typography.Text
                type="secondary"
                style={{ fontFamily: font.mono, fontSize: 11 }}
              >
                {sessionCount} sessions · {toolCallCount} tool calls ·{" "}
                {requestCount} requests
              </Typography.Text>
            )}
          </Space>
          <Button
            type="text"
            size="small"
            icon={<ReloadOutlined spin={loading} />}
            onClick={refetch}
            disabled={loading}
            aria-label="Refresh sessions"
          />
        </Space>
        {loading && sessionRows.length === 0 ? (
          <div style={{ padding: 16, textAlign: "center" }}>
            <Spin size="small" />
          </div>
        ) : sessionRows.length === 0 ? (
          <EmptyState
            title="No recent sessions"
            hint="Traffic through this server will appear here once it's active."
          />
        ) : (
          <Table
            size="small"
            columns={sessionColumns}
            dataSource={sessionRows}
            pagination={false}
            onRow={handleRow}
          />
        )}
      </div>

      <Modal
        open={!!openRecord}
        onCancel={handleCloseRecord}
        title={
          openRecord
            ? `${kindLabel(openRecord.summary.kind)} · ${openRecord.summary.toolName}`
            : undefined
        }
        footer={null}
        width={720}
      >
        {openRecord && (
          <Space direction="vertical" size={12} style={{ width: "100%" }}>
            <Typography.Text type="secondary" style={{ fontFamily: font.mono }}>
              session {openRecord.sessionId} · message{" "}
              {String(openRecord.request.event?.message?.id)} ·{" "}
              {openRecord.summary.durationMs} ms
            </Typography.Text>
            <Typography.Title level={5} style={{ margin: 0 }}>
              Request
            </Typography.Title>
            <pre
              style={{
                background: color.mute,
                padding: 14,
                borderRadius: radius.card,
                overflow: "auto",
                fontSize: 12,
                fontFamily: font.mono,
                lineHeight: 1.5,
                margin: 0,
                maxHeight: 240,
              }}
            >
              {JSON.stringify(openRecord.request, null, 2)}
            </pre>
            <Typography.Title level={5} style={{ margin: 0 }}>
              Response
            </Typography.Title>
            <pre
              style={{
                background: color.mute,
                padding: 14,
                borderRadius: radius.card,
                overflow: "auto",
                fontSize: 12,
                fontFamily: font.mono,
                lineHeight: 1.5,
                margin: 0,
                maxHeight: 240,
              }}
            >
              {JSON.stringify(openRecord.response, null, 2)}
            </pre>
          </Space>
        )}
      </Modal>
    </Space>
  );
};
